<template>
  <section>
    <p
      v-if="firstTime"
      class="text-left"
    >
      Confirm the legal owner(s) of your company so that we can protect your
      account from fraud.
    </p>
    <div class="mb-5">
      <RecognizedOwners :recognized-owners="recognizedOwners" />
      <InvitedOwners :invited-owners="invitedOwners" />
    </div>
    <div v-if="firstTime">
      <div
        class="mt-6 flex gap-3"
        :class="layoutClass"
      >
        <RoundButton
          v-for="button in buttons"
          :key="button.identifier"
          :checked="isActive === button.identifier"
          :use-radio="button.useRadio"
          @click="handleClick(button.identifier)"
        >
          <div class="flex w-full flex-col items-center justify-center">
            <component
              :is="icons[button.identifier]"
              v-if="icons[button.identifier]"
            />
            <p class="text-neutral-subtle-bold">
              {{ button.label }}
            </p>
          </div>
        </RoundButton>
      </div>
      <div class="mt-6 flex justify-center md:justify-start">
        <RoundButton
          :checked="isActive === 'idk'"
          use-radio
          @click="handleClick('idk')"
        >
          <p
            :class="[
              isActive === 'idk' ? 'text-dark-gray' : 'text-neutral-subtle',
            ]"
          >
            I don't know this information
          </p>
        </RoundButton>
      </div>
    </div>
    <section
      v-if="!firstTime && isActive === 'idk'"
      class="opacity-70"
    >
      <RoundButton
        :checked="true"
        :use-radio="true"
        :disabled="true"
        @click="() => {}"
      >
        <p>I don't know this information</p>
      </RoundButton>
    </section>

    <form-navigation-buttons
      v-if="!isNavigationDisabled"
      :enabled="!isNavigationDisabled"
      :component-identifier="componentIdentifier"
      :buttons-to-display="buttonsToDisplay"
      @next="handleNext"
      @back="handleBack"
      @edit="handleEdit"
    />
    <owner-modal
      :visible="addOwnerModal"
      :loading="loading"
      @success="handleInviteOwner"
      @close="closeModal"
    />
    <ConfirmYourOwnership
      :hide-button="true"
      :trigger-flow="confirmOwnershipModal"
      @close="closeModal"
    />
  </section>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useFormRouter } from '@/composables/formRouter';
import { useToast } from 'vue-toast-notification';

import AaoIcon from '@/components/icons/AaoIcon.vue';
import RoundButton from '@/components/RoundButton.vue';
import IaaoIcon from '@/components/icons/IaaoIcon.vue';
import OwnerModal from '@/components/modals/AddOwnerModal.vue';
import FormNavigationButtons from '@/components/FormNavigationButtons.vue';
import ConfirmYourOwnership from '@/components/ConfirmYourOwnership.vue';

import InvitedOwners from '@/components/steps/Ownership/InvitedOwners.vue';
import RecognizedOwners from '@/components/steps/Ownership/RecognizedOwners.vue';

const toast = useToast();
const store = useStore();

const componentIdentifier = 'Ownership-1';
const formRouter = useFormRouter(componentIdentifier);

const firstTime = computed(
  () =>
    store.state.firstTime?.[formRouter.componentStep.value?.firstTimeIdentifier]
);

const onboardingPacketId = computed(() => store.state.carriers.carrierData?.id);

const activeStep = computed(
  () => store.state.formRoutes?.activeStep?.identifier
);

const isNavigationDisabled = computed(() => formRouter.isDisabled.value);

const invitedOwners = computed(
  () =>
    store.state.ownership.ownershipData?.ownership_users?.filter(
      (owner) => owner?.status === 'invited'
    ) || []
);
const recognizedOwners = computed(
  () =>
    store.state.ownership.ownershipData?.ownership_users?.filter(
      (owner) => owner?.status === 'complete' || owner?.details_confirmed_at
    ) || []
);

const userOwnership = computed(
  () =>
    store.state.ownership.ownershipData?.ownership_users?.find(
      (owner) => owner.user_id === store.state.users.currentUser.id
    ) || null
);

const isSubmitButtonDisabled = computed(() => store.state.submitButtonDisabled);
const ownershipData = computed(() => store.state.ownership.ownershipData);

const owners = computed(() => [
  ...(invitedOwners?.value || []),
  ...(recognizedOwners?.value || []),
]);

const isActive = ref('');
const loading = ref(false);
const addOwnerModal = ref(false);
const confirmOwnershipModal = ref(false);

const layoutClass = 'flex-col md:flex-row';
const buttonsToDisplay = computed(() =>
  firstTime.value ? 'continue-save' : 'continue-edit'
);

const icons = { aao: AaoIcon, iaao: IaaoIcon };

const buttons = computed(() => {
  const options = [];

  if (!userOwnership?.value?.job_title) {
    options.push({
      identifier: 'iaao',
      label: 'I am an Owner',
      useRadio: false,
    });
  }

  options.push({
    identifier: 'aao',
    label: 'Add an Owner',
    useRadio: false,
  });

  return options;
});

const handleClick = (identifier) => {
  isActive.value = identifier;

  const modalMap = {
    iaao: () => (confirmOwnershipModal.value = true),
    aao: () => (addOwnerModal.value = true),
  };

  modalMap[identifier]?.();

  if (invitedOwners.value.length || recognizedOwners.value.length) return;
  store.commit('toggleDisabledButton', {
    bool: identifier !== 'idk',
    source: `OwnershipStepOne #${identifier === 'idk' ? 7 : 8}`,
  });
};

const createOwnershipCompany = async () =>
  store.dispatch('ownership/createOwnershipCompany', {
    companyId: store.state.users.currentUser.company.id,
  });

const handleInviteOwner = async ({ email }) => {
  if (!email) {
    toast?.open({
      message: 'Please provide an email for the owner',
      position: 'top-right',
      duration: 5000,
    });
    return;
  }
  try {
    loading.value = true;
    if (!ownershipData.value) await createOwnershipCompany();
    const { id: ownershipId } = ownershipData.value || {};

    await store.dispatch('ownership/createOwnershipUser', {
      ownershipId,
      email: email?.toLowerCase(),
      type: 'owner',
    });

    await store.dispatch(
      'ownership/getOwnershipByCompanyId',
      store.state.users.currentUser.company.id
    );

    if (isSubmitButtonDisabled.value) {
      store.commit('toggleDisabledButton', {
        bool: false,
        source: 'OwnershipStepOne #5',
      });
    }

    toast?.open({
      message: 'Owner has been invited',
      position: 'top-right',
      duration: 5000,
    });
  } catch (error) {
    toast?.open({
      message:
        error?.response?.data?.[0] ||
        'Something went wrong with your request. Please try again',
      position: 'top-right',
      duration: 5000,
    });
  } finally {
    loading.value = false;
    closeModal();
  }
};

const closeModal = () => {
  addOwnerModal.value = false;
  confirmOwnershipModal.value = false;
  isActive.value = '';
};

const handleBack = () => formRouter.moveToPreviousStep();
const handleNext = async () => {
  const selectedOption = isActive.value;
  const storedIdk =
    localStorage.getItem(`ownership.step1.idk.${onboardingPacketId.value}`) ===
    'idk';

  if (selectedOption !== 'idk' && storedIdk)
    localStorage.removeItem(`ownership.step1.idk.${onboardingPacketId.value}`);

  if (selectedOption === 'idk' && !storedIdk) {
    await store.dispatch(
      'ownership/emailForOwners',
      store.state.carriers.carrierData.connection.id
    );

    localStorage.setItem(
      `ownership.step1.idk.${onboardingPacketId.value}`,
      'idk'
    );
  }
  formRouter.moveToNextStep();
};
const handleEdit = () => {
  store.commit(
    'addFormDebugMessage',
    `${componentIdentifier}: 'handleEdit' called`
  );
  if (isNavigationDisabled.value) {
    console.log(
      `ERROR: ${componentIdentifier}: formRouter is disabled, returning`
    );
    return;
  }
  formRouter.editActiveStep();
};

const shouldDisableSaveButton = () => {
  const invitedCount = invitedOwners?.value?.length || 0;
  const recognizedCount = recognizedOwners?.value?.length || 0;
  const idkWasSelected =
    localStorage.getItem(`ownership.step1.idk.${onboardingPacketId.value}`) ===
    'idk';
  const isIncludedOnInvited = invitedOwners.value.some(
    (owner) => owner.user_id === store.state.users.currentUser.id
  );

  const existsAControllingManager = owners.value.some(
    (owner) => owner.is_type === 'controlling_manager'
  );

  if (idkWasSelected) isActive.value = 'idk';

  if (existsAControllingManager && owners?.value.length == 1) {
    store.commit('firstTime/updateStepFirstTime', {
      step: 'Ownership',
      value: true,
    });
    store.commit('toggleDisabledButton', {
      bool: false,
      source: 'OwnershipStepOne #2',
    });
    return;
  }

  // If the logged-in user is part of the invited ownership, use the first time
  // to force them to confirm their identity.

  if (isIncludedOnInvited && userOwnership.value?.job_title === null) {
    store.commit('firstTime/updateStepFirstTime', {
      step: 'Ownership',
      value: true,
    });
    store.commit('toggleDisabledButton', {
      bool: false,
      source: 'OwnershipStepOne #2',
    });
    return;
  }

  if (invitedCount > 0 || recognizedCount > 0 || idkWasSelected) {
    store.commit('firstTime/updateStepFirstTime', {
      step: 'Ownership',
      value: false,
    });
    store.commit('toggleDisabledButton', {
      bool: false,
      source: 'OwnershipStepOne #2',
    });
    return;
  }

  if (!invitedCount && !recognizedCount && !idkWasSelected) {
    store.commit('firstTime/updateStepFirstTime', {
      step: 'Ownership',
      value: true,
    });
    store.commit('toggleDisabledButton', {
      bool: true,
      source: 'OwnershipStepOne #1',
    });
  } else if (!isActive.value) {
    store.commit('toggleDisabledButton', {
      bool: true,
      source: 'OwnershipStepOne #3',
    });
  }
};

watch(owners, async (newVal) => {
  if (newVal.length > 0) {
    if (isSubmitButtonDisabled.value) {
      store.commit('toggleDisabledButton', {
        bool: false,
        source: 'OwnershipStepOne #10',
      });
    }
  }
});

watch(activeStep, async (newVal) => {
  if (newVal !== componentIdentifier) return;
  try {
    await store.dispatch(
      'ownership/getOwnershipByCompanyId',
      store.state.users.currentUser.company.id
    );
  } catch (e) {
    let status = e.response?.status;
    if (status && [401, 403, 422].includes(status)) {
      throw e;
    }
    console.warn('Non-critical error in getOwnershipByCompanyId:', e);
  }

  shouldDisableSaveButton();
});
</script>
